"use client";

import * as React from "react";
import { useRouter } from "@/lib/router/navigation";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { ProjectSidebar } from "./project-sidebar";
import { SkipLink } from "@/components/shell/skip-link";
import { AppMain } from "@/components/shell/app-main";
import { Topbar } from "./topbar";
import { CommandPalette } from "./command-palette";
import { useTenancy } from "@/lib/tenancy/tenancy-context";

/** Client shell for a project route: syncs the URL's projectId into context. */
export function ProjectShell({ projectId, children }: { projectId: string; children: React.ReactNode }) {
  const router = useRouter();
  const { projectId: active, setProjectId, projects, loading } = useTenancy();

  // Drive the active project from the URL.
  React.useEffect(() => {
    if (projectId && projectId !== active) setProjectId(projectId);
  }, [projectId, active, setProjectId]);

  // Unknown project → back to the picker.
  React.useEffect(() => {
    if (!loading && projects.length > 0 && !projects.some((w) => w.id === projectId)) {
      router.replace("/projects");
    }
  }, [loading, projects, projectId, router]);

  return (
    <SidebarProvider style={{ "--sidebar-width": "236px", "--sidebar-width-icon": "56px" } as React.CSSProperties}>
      <SkipLink />
      <ProjectSidebar />
      <SidebarInset className="min-w-0 bg-canvas">
        <Topbar />
        <AppMain>
          <div className="mx-auto w-full max-w-[1360px] p-4 lg:p-6">{children}</div>
        </AppMain>
      </SidebarInset>
      <CommandPalette />
    </SidebarProvider>
  );
}
